// src/components/UserAvatar.js

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { fixImageUrl } from '../services/imageService';

/**
 * Reusable component for displaying a user with their avatar
 * Fallback: shows initials in a colored circle when no image is available
 * Now with image loading error handling and configuration for mobile-friendly display
 *
 * @param {Object} props Component props
 * @param {string} props.profileImage URL to user's profile image
 * @param {string} props.username Username (optional, for backwards compatibility)
 * @param {string} props.displayName User's display name (optional)
 * @param {string} props.email User's email (used as fallback for display)
 * @param {string} props.timestamp Timestamp to display (optional)
 * @param {string} props.timestampFormat Formatted timestamp string (optional)
 * @param {number} props.size Size of the avatar in pixels (default: 40)
 * @param {ReactNode} props.actions Additional actions to display (e.g., dropdown menu)
 * @param {string} props.className Additional CSS classes for the container
 * @param {boolean} props.showName Whether to show the name next to avatar (default: true)
 * @param {boolean} props.avatarOnly If true, only shows the avatar with no text (default: false)
 */
function UserAvatar({
  profileImage,
  username,
  displayName,
  email,
  timestamp,
  timestampFormat,
  size = 40,
  actions,
  className = '',
  showName = true,
  avatarOnly = false
}) {
  const [imageError, setImageError] = useState(false);

  // Get user's initials from display name or email
  const getInitials = () => {
    const name = displayName || (email ? email.split('@')[0] : '') || username || '';

    // If no name is available, return a default
    if (!name) return '?';

    // Split the name by spaces and get the first letter of each part
    const parts = name.split(' ');

    if (parts.length === 1) {
      // If just one word, return the first letter (or first two if available)
      return name.length > 1 ? name.substring(0, 2).toUpperCase() : name.substring(0, 1).toUpperCase();
    } else {
      // Get first letter of first and last parts
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    }
  };
  
  // Generate a consistent color based on displayName or email
  const getAvatarColor = () => {
    const colorPalette = [
      '#1E88E5', // Blue
      '#43A047', // Green
      '#E53935', // Red
      '#FDD835', // Yellow
      '#8E24AA', // Purple
      '#FB8C00', // Orange
      '#00ACC1', // Teal
      '#3949AB', // Indigo
      '#7CB342', // Light Green
      '#C0CA33', // Lime
      '#FFB300', // Amber
      '#F4511E', // Deep Orange
      '#6D4C41', // Brown
      '#757575', // Grey
      '#546E7A'  // Blue Grey
    ];

    // Use the sum of character codes in displayName or email to pick a color
    const name = displayName || email || username || '';
    let sum = 0;
    for (let i = 0; i < name.length; i++) {
      sum += name.charCodeAt(i);
    }

    return colorPalette[sum % colorPalette.length];
  };

  // Handle image loading errors
  const handleImageError = () => {
    setImageError(true);
  };

  // Fix the image URL using the same method as EventCard
  const fixedImageUrl = profileImage ? fixImageUrl(profileImage) : null;

  // If avatar only, just return the avatar
  if (avatarOnly) {
    return (
      <div className={className}>
        {fixedImageUrl && !imageError ? (
          <img
            src={fixedImageUrl}
            alt={displayName || (email ? email.split('@')[0] : '') || username}
            className="rounded-circle"
            width={size}
            height={size}
            style={{ objectFit: 'cover' }}
            onError={handleImageError}
          />
        ) : (
          <div 
            className="rounded-circle d-flex justify-content-center align-items-center"
            style={{
              width: size,
              height: size,
              backgroundColor: getAvatarColor(),
              color: 'white',
              fontSize: size * 0.4,
              fontWeight: 'bold'
            }}
          >
            {getInitials()}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`d-flex justify-content-between align-items-start ${className}`}>
      <div className="d-flex">
        <div className={showName ? "me-3" : ""}>
          {fixedImageUrl && !imageError ? (
            // Show profile image if available and not errored
            <img
              src={fixedImageUrl}
              alt={displayName || (email ? email.split('@')[0] : '') || username}
              className="rounded-circle"
              width={size}
              height={size}
              style={{ objectFit: 'cover' }}
              onError={handleImageError}
            />
          ) : (
            // Show initials in a colored circle if no image or on error
            <div 
              className="rounded-circle d-flex justify-content-center align-items-center"
              style={{
                width: size,
                height: size,
                backgroundColor: getAvatarColor(),
                color: 'white',
                fontSize: size * 0.4,
                fontWeight: 'bold'
              }}
            >
              {getInitials()}
            </div>
          )}
        </div>
        {showName && (
          <div>
            {displayName !== null && (
              <h5 className="mb-0">{displayName || (email ? email.split('@')[0] : '') || username}</h5>
            )}
            {timestamp && (
              <p className="text-muted mb-0 small">{timestampFormat || timestamp}</p>
            )}
          </div>
        )}
      </div>
      
      {actions && (
        <div className="user-actions">
          {actions}
        </div>
      )}
    </div>
  );
}

UserAvatar.propTypes = {
  profileImage: PropTypes.string,
  username: PropTypes.string,
  displayName: PropTypes.string,
  email: PropTypes.string,
  timestamp: PropTypes.string,
  timestampFormat: PropTypes.string,
  size: PropTypes.number,
  actions: PropTypes.node,
  className: PropTypes.string,
  showName: PropTypes.bool,
  avatarOnly: PropTypes.bool
};

export default UserAvatar;